import { appState, confirmationAlertProps } from '../../../interfaces/common';
import { Button, Checkbox, Flex, Grid, Image, Label, ModalManagerContext, Text } from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import * as Logos from '../../../constants/images';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { fetchAllConfigurationRecords, updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import CountryCodeAutoComplete from '../../Utilities/CountryCodeAutoComplete';
import { tokenMaskerStyle } from '../../Utilities/custom.styled';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import MenuReplyConfiguration from '../../Utilities/MenuReplyConfiguration';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { deleteAccount, getAccountDetails, updateAccountDetails, updateOutboundMessagingEnabled } from './SMSApis';
import RealTimeTranslation from '../Chat/Tabs/LanguageTab/RealTimeTranslation';

type AccountDetails = {
  accountSid: string;
  authToken: string;
  lastUpdate: string;
  name: string;
  phoneNumber: string;
  menuConfiguration?: string;
  outboundMessagingEnabled?: boolean;
  translationServiceId: string;
  translateFromLanguages: string[];
  primaryLanguage: string;
  languageTranslationEnabled: boolean;
};

const SMS: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = {
    accountSid: '',
    authToken: '',
    lastUpdate: '',
    name: '',
    phoneNumber: '',
    menuConfiguration: '',
    translationServiceId: '',
    translateFromLanguages: [],
    primaryLanguage: '',
    languageTranslationEnabled: false,
  };
  const [accountDetails, setaccountDetails] = useState(initialState);
  const [countryCodes, setCountryCodes] = useState<{
    [country: string]: string;
  }>({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.name,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };
  const [isMaskEnabled, setIsMaskEnabled] = useState(true);
  const { configRecords } = useSelector((state: appState) => state);

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
      if (response?.data?.countryCodes) {
        setCountryCodes(response?.data?.countryCodes);
      }
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const updateCountryCodes = useCallback(
    async (countries: { [country: string]: string }) => {
      if (Object.keys(countries).length === 0) {
        dispatch(updateBannerInfo(translate('country_code_dropdown_error_info'), 'urgent'));
        return;
      }
      dispatch(updateLoaderDetails(true));
      let response = await updateAccountDetails(sessionId, connectionId, countries);
      dispatch(updateLoaderDetails(false));
      if (response?.status === 200) {
        dispatch(updateBannerInfo(translate('updated_country_codes'), 'success'));
        if (response?.data?.countryCodes) {
          setCountryCodes(response?.data?.countryCodes);
        }
      } else if (response?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
      } else {
        dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
      }
    },
    [sessionId, connectionId, dispatch, navigate, translate],
  );

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`Twilio ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [sessionId, connectionId, dispatch, navigate, translate]);

  const updateOutboundMessaging = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const outboundMessagingEnabled = event.target.checked;
      dispatch(updateLoaderDetails(true));
      const response = await updateOutboundMessagingEnabled(sessionId, connectionId, outboundMessagingEnabled);
      dispatch(updateLoaderDetails(false));
      if (response?.status === 200) {
        setaccountDetails((currentAccountDetails) => ({
          ...currentAccountDetails,
          ...(response.data || {}),
          outboundMessagingEnabled: response.data?.outboundMessagingEnabled ?? outboundMessagingEnabled,
        }));
        dispatch(
          updateBannerInfo(
            translate(
              outboundMessagingEnabled ? 'outbound_messaging_enable_success' : 'outbound_messaging_disable_success',
            ),
            'success',
          ),
        );
      } else if (response?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        dispatch(updateBannerInfo(translate('failed_to_update_twilio'), 'urgent'));
      }
    },
    [sessionId, connectionId, dispatch, navigate, translate],
  );

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  useEffect(() => {
    dispatch(fetchAllConfigurationRecords());
  }, [dispatch]);

  const getMaskedToken = (token: string = '') => {
    return !isMaskEnabled ? token : new Array(token.length).fill('*').join('');
  };

  const headerElement = () => {
    return (
      accountDetails?.name && (
        <Text variant="h1" as="h1">
          {accountDetails?.name}
        </Text>
      )
    );
  };

  const [translationEnablerError, setTranslationEnablerError] = useState(false);

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.name && (
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'end',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'end',
              }}
            >
              <Text variant="secondary">{translate('Twilio SMS')}</Text>
              <Image alt="Twitter" src={Logos.SMSLogo} width="30px" height="40px" />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'end',
            }}
          >
            <Label>{translate('phone_number')}</Label>
            <Text variant="secondary">{accountDetails?.phoneNumber}</Text>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(3, 33%)',
              gap: 1,
            }}
          >
            <Flex>
              <Label>{translate('account_sid')}</Label>
            </Flex>
            <Flex>
              <Text variant="secondary">{accountDetails?.accountSid}</Text>
            </Flex>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'end',
            }}
          >
            <Label>{translate('auth_token')}</Label>
            <Flex container={{ itemGap: 2, alignItems: 'end' }}>
              <Text variant="secondary">{getMaskedToken(accountDetails?.authToken)}</Text>
              <Button style={tokenMaskerStyle} onClick={() => setIsMaskEnabled(!isMaskEnabled)}>
                {translate(isMaskEnabled ? 'show_token' : 'hide_token')}
              </Button>
            </Flex>
          </Grid>

          <Flex
            container={{
              direction: 'column',
              itemGap: 3,
            }}
          >
            <Grid
              container={{
                cols: 'repeat(2, 33% 67%)',
                gap: 1,
                alignItems: 'end',
              }}
            >
              <Label>{translate('webhook')}</Label>
              <Text variant="secondary">{process.env.REACT_APP_INCOMING_WEBHOOK}/twilio</Text>
            </Grid>
            {process.env.REACT_APP_ENABLE_OUTBOUND_MESSAGING_FOR_SMS && (
              <Checkbox
                label={translate('enable_outbound_messaging')}
                name="outboundMessagingEnabled"
                checked={accountDetails.outboundMessagingEnabled ?? false}
                onChange={updateOutboundMessaging}
              />
            )}
            <MenuReplyConfiguration
              menuReplyConfiguration={accountDetails.menuConfiguration}
              source="sms"
              connectionId={connectionId}
            />
            <CountryCodeAutoComplete countryCodes={countryCodes} setCountryCodes={updateCountryCodes} />
          </Flex>
          {configRecords && Object.keys(configRecords).length > 0 && (
            <RealTimeTranslation
              translationEnablerError={translationEnablerError}
              setTranslationEnablerError={setTranslationEnablerError}
              connectionId={connectionId}
              showPrimaryLanguage={true}
              languageTranslationEnabled={accountDetails.languageTranslationEnabled}
              translateFromLanguages={accountDetails.translateFromLanguages}
              translationServiceId={accountDetails.translationServiceId}
              translateToLanguage={accountDetails.primaryLanguage}
            />
          )}
          <Flex>
            <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default SMS;
